import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Download, 
  Code, 
  CheckCircle, 
  AlertCircle, 
  Loader2, 
  ExternalLink,
  Smartphone,
  Globe,
  DollarSign,
  TrendingUp,
  Copy,
  Check,
  Info,
  FileText,
  Zap,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { BACKEND_CONFIG } from '../utils/productionConfig';
import { useAuthContext } from '../contexts/AuthContext';

const API_URL = BACKEND_CONFIG.apiUrl;

export function WordPressPluginDownload() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pluginInfo, setPluginInfo] = useState<any>(null);
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Check if user is affiliate enabled
  useEffect(() => {
    if (user && !user.isAffiliateEnabled) {
      toast.error('You need affiliate access to download WordPress plugin. Contact admin.');
      navigate('/refer-earn');
    }
  }, [user, navigate]);

  // Show access denied if not enabled
  if (!user?.isAffiliateEnabled) {
    return (
      <Card className="max-w-2xl mx-auto mt-20">
        <CardContent className="p-12 text-center">
          <Lock className="h-20 w-20 mx-auto text-yellow-600 mb-6" />
          <h2 className="text-3xl font-bold mb-4">Affiliate Access Required</h2>
          <p className="text-gray-600 mb-6 text-lg">
            You need to be approved as an affiliate to download WordPress plugin.
            <br />
            Please contact admin to enable affiliate access for your account.
          </p>
          <Button 
            onClick={() => navigate('/refer-earn')}
            className="bg-gradient-to-r from-pink-500 to-rose-500"
          >
            Go to Referral & Wallet
          </Button>
        </CardContent>
      </Card>
    );
  }

  useEffect(() => {
    fetchPluginInfo();
  }, []);

  const fetchPluginInfo = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/wordpress-plugin/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setPluginInfo(response.data.data);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch plugin info');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_URL}/wordpress-plugin/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'atootbandan-matrimonial-ads.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Plugin downloaded successfully!');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to download plugin');
    } finally {
      setDownloading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="size-8 animate-spin text-rose-500" />
      </div>
    );
  }

  if (!pluginInfo?.hasAffiliateId) {
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardHeader>
          <div className="flex items-start gap-3">
            <div className="p-3 bg-amber-100 rounded-lg">
              <AlertCircle className="size-6 text-amber-600" />
            </div>
            <div>
              <CardTitle className="text-amber-900">Affiliate Account Required</CardTitle>
              <CardDescription className="text-amber-700">
                You need to enable your affiliate account first to download the WordPress plugin
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-amber-800 mb-4">
            To get started with the WordPress plugin and earn commissions:
          </p>
          <ol className="list-decimal list-inside space-y-2 text-sm text-amber-800 mb-4">
            <li>Go to "Referral & Wallet" section</li>
            <li>Enable your referral/affiliate account</li>
            <li>Get your unique affiliate ID</li>
            <li>Come back here to download the plugin</li>
          </ol>
          <Button className="bg-amber-600 hover:bg-amber-700 text-white">
            Go to Referral & Wallet
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="border-2 border-rose-200 bg-gradient-to-br from-rose-50 to-pink-50">
        <CardHeader>
          <div className="flex items-start gap-4">
            <div className="p-4 bg-gradient-to-br from-rose-500 to-pink-500 rounded-xl shadow-lg">
              <Download className="size-8 text-white" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-2xl">WordPress Plugin</CardTitle>
              <CardDescription className="text-base mt-2">
                Download and install our plugin on your WordPress site to display matrimonial ads and earn commissions
              </CardDescription>
              <div className="flex flex-wrap gap-2 mt-4">
                <Badge className="bg-green-100 text-green-700 border-green-300">
                  <CheckCircle className="size-3 mr-1" />
                  Ready to Download
                </Badge>
                <Badge className="bg-blue-100 text-blue-700 border-blue-300">
                  Version {pluginInfo?.pluginVersion || '1.0.0'}
                </Badge>
                <Badge className="bg-purple-100 text-purple-700 border-purple-300">
                  <Zap className="size-3 mr-1" />
                  Auto-configured
                </Badge>
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Affiliate ID Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="size-5 text-rose-500" />
            Your Affiliate Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-gray-50 rounded-lg border">
            <label className="text-sm font-medium text-gray-700 block mb-2">
              Your Affiliate ID (Pre-configured in Plugin)
            </label>
            <div className="flex items-center gap-2">
              <code className="flex-1 p-3 bg-white border rounded font-mono text-sm">
                {pluginInfo?.affiliateId}
              </code>
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(pluginInfo?.affiliateId)}
              >
                {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
              </Button>
            </div>
            <p className="text-xs text-gray-600 mt-2">
              This ID is automatically embedded in the plugin. All clicks and commissions will be tracked to your account.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="size-5 text-blue-600" />
                <span className="font-semibold text-blue-900">Automatic Tracking</span>
              </div>
              <p className="text-sm text-blue-700">
                Every click is tracked with your affiliate ID
              </p>
            </div>

            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="size-5 text-green-600" />
                <span className="font-semibold text-green-900">Earn Commissions</span>
              </div>
              <p className="text-sm text-green-700">
                Get paid when visitors purchase plans
              </p>
            </div>

            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="flex items-center gap-2 mb-2">
                <Smartphone className="size-5 text-purple-600" />
                <span className="font-semibold text-purple-900">Responsive Ads</span>
              </div>
              <p className="text-sm text-purple-700">
                Beautiful ads on all devices
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Download Section */}
      <Card>
        <CardHeader>
          <CardTitle>Download Plugin</CardTitle>
          <CardDescription>
            Get the latest version of our WordPress plugin with your affiliate ID pre-configured
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button
            onClick={handleDownload}
            disabled={downloading}
            size="lg"
            className="w-full bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700 text-white text-base"
          >
            {downloading ? (
              <>
                <Loader2 className="size-5 mr-2 animate-spin" />
                Preparing Download...
              </>
            ) : (
              <>
                <Download className="size-5 mr-2" />
                Download WordPress Plugin (.zip)
              </>
            )}
          </Button>

          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start gap-3">
              <Info className="size-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="space-y-2 text-sm text-blue-800">
                <p className="font-medium">What happens after download:</p>
                <ol className="list-decimal list-inside space-y-1 ml-2">
                  <li>Upload the ZIP file to your WordPress site</li>
                  <li>Activate the plugin from WordPress admin</li>
                  <li>Your affiliate ID is already configured</li>
                  <li>Start displaying ads and earning commissions!</li>
                </ol>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Installation Guide */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="size-5 text-rose-500" />
            Installation Guide
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Step 1 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-rose-500 text-white rounded-full flex items-center justify-center font-bold">
                  1
                </div>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold mb-2">Download the Plugin</h3>
                <p className="text-sm text-gray-600 mb-2">
                  Click the download button above to get the atootbandan-matrimonial-ads.zip file
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-rose-500 text-white rounded-full flex items-center justify-center font-bold">
                  2
                </div>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold mb-2">Upload to WordPress</h3>
                <p className="text-sm text-gray-600 mb-2">
                  In your WordPress admin panel:
                </p>
                <ol className="list-disc list-inside space-y-1 text-sm text-gray-600 ml-4">
                  <li>Go to Plugins → Add New</li>
                  <li>Click "Upload Plugin" button</li>
                  <li>Choose the downloaded ZIP file</li>
                  <li>Click "Install Now"</li>
                </ol>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-rose-500 text-white rounded-full flex items-center justify-center font-bold">
                  3
                </div>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold mb-2">Activate Plugin</h3>
                <p className="text-sm text-gray-600 mb-2">
                  After installation, click the "Activate" button
                </p>
                <div className="p-3 bg-green-50 rounded border border-green-200 text-sm text-green-800">
                  <CheckCircle className="size-4 inline mr-1" />
                  Your affiliate ID will be automatically configured!
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-rose-500 text-white rounded-full flex items-center justify-center font-bold">
                  4
                </div>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold mb-2">Display Ads</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Add ads to your site using shortcodes or widgets:
                </p>
                
                <div className="space-y-3">
                  <div className="p-3 bg-gray-50 rounded border">
                    <p className="text-sm font-medium mb-1">In Posts/Pages:</p>
                    <code className="block p-2 bg-white border rounded text-xs">
                      [atootbandan_ad]
                    </code>
                  </div>

                  <div className="p-3 bg-gray-50 rounded border">
                    <p className="text-sm font-medium mb-1">In Theme Files:</p>
                    <code className="block p-2 bg-white border rounded text-xs">
                      {'<?php echo do_shortcode(\'[atootbandan_ad]\'); ?>'}
                    </code>
                  </div>

                  <div className="p-3 bg-gray-50 rounded border">
                    <p className="text-sm font-medium mb-1">With Custom Size:</p>
                    <code className="block p-2 bg-white border rounded text-xs">
                      [atootbandan_ad size="large"]
                    </code>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 5 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-rose-500 text-white rounded-full flex items-center justify-center font-bold">
                  5
                </div>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold mb-2">Track Your Earnings</h3>
                <p className="text-sm text-gray-600 mb-2">
                  Monitor your performance:
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 ml-4">
                  <li>WordPress Admin: Go to Atootbandan Ads menu</li>
                  <li>Our Platform: Check Referral & Wallet section</li>
                  <li>View real-time clicks and earnings</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Features Card */}
      <Card>
        <CardHeader>
          <CardTitle>Plugin Features</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="size-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Auto-configured Affiliate ID</p>
                <p className="text-sm text-gray-600">Pre-set with your unique affiliate code</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle className="size-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Multiple Ad Sizes</p>
                <p className="text-sm text-gray-600">Small, Medium, Large, and Responsive</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle className="size-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Automatic Click Tracking</p>
                <p className="text-sm text-gray-600">Every click is tracked for commissions</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle className="size-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Realtime Statistics</p>
                <p className="text-sm text-gray-600">View clicks and earnings in WordPress admin</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle className="size-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Shortcode Support</p>
                <p className="text-sm text-gray-600">Easy placement anywhere on your site</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle className="size-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Widget Support</p>
                <p className="text-sm text-gray-600">Drag & drop in sidebars and widget areas</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle className="size-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Mobile Responsive</p>
                <p className="text-sm text-gray-600">Beautiful on all screen sizes</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle className="size-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">No Configuration Needed</p>
                <p className="text-sm text-gray-600">Install, activate, and start earning</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Publishing to WordPress.org Guide */}
      <Card className="border-purple-200 bg-purple-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-purple-900">
            <Globe className="size-5" />
            Publishing to WordPress.org (Optional)
          </CardTitle>
          <CardDescription className="text-purple-700">
            Want to publish this plugin to WordPress.org repository? Here's how:
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-white rounded-lg border border-purple-200">
            <h4 className="font-semibold mb-2">Requirements:</h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
              <li>Create a WordPress.org account</li>
              <li>The plugin must be GPL licensed (already included)</li>
              <li>Submit plugin for review via WordPress.org SVN</li>
              <li>Wait for approval (usually 2-5 days)</li>
            </ul>
          </div>

          <Button variant="outline" className="w-full" asChild>
            <a href="https://wordpress.org/plugins/developers/" target="_blank" rel="noopener noreferrer">
              <ExternalLink className="size-4 mr-2" />
              Learn More About WordPress Plugin Submission
            </a>
          </Button>

          <div className="p-3 bg-amber-50 rounded border border-amber-200 text-sm text-amber-800">
            <Info className="size-4 inline mr-1" />
            <strong>Note:</strong> Publishing to WordPress.org is optional. You can use the plugin on your own sites immediately after download.
          </div>
        </CardContent>
      </Card>

      {/* Support Card */}
      <Card>
        <CardHeader>
          <CardTitle>Need Help?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-gray-600">
            Having trouble with installation or configuration? We're here to help!
          </p>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" size="sm">
              View Documentation
            </Button>
            <Button variant="outline" size="sm">
              Contact Support
            </Button>
            <Button variant="outline" size="sm">
              Watch Video Tutorial
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
